// Helper GlitchTip/Sentry lato frontend (portato da 4all, invariato) [sc-47544].
// - sanitizeSentryEvent: beforeSend difensivo (rimuove PII/segreti, query string).
// - getLastSentryEventId: id ultimo evento (per correlazione col Modulo Assistenza), null se assente.
// Nessuna di queste funzioni deve MAI lanciare o bloccare l'app.
import * as Sentry from "@sentry/vue";

// Chiavi sensibili da filtrare in strutture semplici (headers, cookies, extra, contexts).
const SENSITIVE_KEYS = [
  "authorization",
  "cookie",
  "token",
  "password",
  "secret",
  "credential",
  "session",
  "apikey",
];

function isSensitiveKey(key) {
  const k = String(key)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  return SENSITIVE_KEYS.some((s) => k.includes(s));
}

// Rimuove query string / fragment da un URL, mantenendo il path.
function stripQuery(url) {
  if (typeof url !== "string") return url;
  const i = url.search(/[?#]/);
  return i === -1 ? url : url.slice(0, i);
}

// Filtra header sensibili (authorization, cookie, ...).
function filterHeaders(headers) {
  if (!headers || typeof headers !== "object") return headers;
  const out = Array.isArray(headers) ? [] : {};
  for (const [k, v] of Object.entries(headers)) {
    out[k] = isSensitiveKey(k) ? "[Filtered]" : v;
  }
  return out;
}

// Filtra le chiavi sensibili di primo livello di un oggetto semplice (in place).
function filterTopLevel(obj) {
  if (!obj || typeof obj !== "object") return;
  for (const k of Object.keys(obj)) {
    if (isSensitiveKey(k)) obj[k] = "[Filtered]";
  }
}

// Chiavi il cui valore è tipicamente una URL/path: rimuovere sempre query/fragment.
const URL_KEYS = new Set([
  "url",
  "href",
  "to",
  "from",
  "path",
  "route",
  "location",
]);

// Coppie chiave=valore sensibili da redigere anche dentro stringhe/messaggi.
const SENSITIVE_QUERY_RE =
  /\b(token|email|password|secret|authorization|cookie|session|apikey)=[^&\s#]*/gi;

// Sanifica un valore stringa: redige coppie sensibili e rimuove query/fragment
// dalle stringhe che sembrano URL o path. Non lancia mai.
function sanitizeStringValue(value) {
  if (typeof value !== "string") return value;
  let out = value;
  const looksLikeUrl = out.startsWith("/") || /^https?:\/\//i.test(out);
  if (looksLikeUrl && (out.includes("?") || out.includes("#"))) {
    out = stripQuery(out);
  }
  out = out.replace(
    SENSITIVE_QUERY_RE,
    (m) => m.slice(0, m.indexOf("=") + 1) + "[Filtered]"
  );
  return out;
}

// Sanifica breadcrumb.data (shallow, con piccola ricorsione guardata). In place.
function sanitizeBreadcrumbData(data, depth = 0) {
  if (!data || typeof data !== "object" || depth > 2) return data;
  for (const k of Object.keys(data)) {
    const v = data[k];
    if (isSensitiveKey(k)) {
      data[k] = "[Filtered]";
    } else if (typeof v === "string") {
      if (URL_KEYS.has(k.toLowerCase())) {
        data[k] = sanitizeStringValue(stripQuery(v));
      } else {
        data[k] = sanitizeStringValue(v);
      }
    } else if (v && typeof v === "object") {
      sanitizeBreadcrumbData(v, depth + 1);
    }
  }
  return data;
}

// Sanifica l'array dei breadcrumbs (mutazione in place). Mai lancia; non elimina
// i breadcrumb, li pulisce soltanto.
function sanitizeBreadcrumbs(breadcrumbs) {
  if (!Array.isArray(breadcrumbs)) return breadcrumbs;
  for (const bc of breadcrumbs) {
    try {
      if (!bc || typeof bc !== "object") continue;
      if (bc.data && typeof bc.data === "object") {
        bc.data = sanitizeBreadcrumbData(bc.data);
      }
      if (typeof bc.message === "string") {
        bc.message = sanitizeStringValue(bc.message);
      }
    } catch {
      /* non rimuovere il breadcrumb: al massimo resta non sanificato */
    }
  }
  return breadcrumbs;
}

// beforeSend: difesa a livello SDK. Non lancia mai; in caso di errore ritorna
// comunque l'evento (originale) per non perdere segnalazioni valide.
export function sanitizeSentryEvent(event) {
  try {
    if (event && event.request) {
      const req = event.request;
      if (req.url) req.url = stripQuery(req.url);
      if (req.query_string) delete req.query_string;
      if (req.headers) req.headers = filterHeaders(req.headers);
      if (req.cookies) req.cookies = "[Filtered]";
    }
    if (event && event.extra) filterTopLevel(event.extra);
    if (event && event.contexts) {
      for (const ctx of Object.values(event.contexts)) {
        filterTopLevel(ctx);
      }
    }
    // Breadcrumbs automatici (es. navigazione router): possono contenere URL con
    // query string / PII. Sanifica sia l'array diretto sia la forma { values: [] }.
    if (event && event.breadcrumbs) {
      if (Array.isArray(event.breadcrumbs)) {
        sanitizeBreadcrumbs(event.breadcrumbs);
      } else if (Array.isArray(event.breadcrumbs.values)) {
        sanitizeBreadcrumbs(event.breadcrumbs.values);
      }
    }
    return event;
  } catch {
    // Non bloccare mai l'invio per un errore del sanitizer.
    return event;
  }
}

// Ultimo event id di Sentry, per correlazione futura col ticket. null se non disponibile.
export function getLastSentryEventId() {
  try {
    return Sentry.lastEventId() || null;
  } catch {
    return null;
  }
}

// Prepara in modo PRUDENTE l'eventId GlitchTip da allegare al ticket [sc-47917].
//
// Problema: Sentry.lastEventId() restituisce un id generato LATO CLIENT anche se
// l'envelope non è mai stato consegnato → il ticket finiva per puntare a eventi
// che GlitchTip non ha mai ricevuto (falsi link).
//
// Qui, prima di consegnare l'id, tentiamo un flush breve della coda di trasporto:
//   - nessun lastEventId               → "" (niente da correlare)
//   - flush assente/non-funzione       → "" (non possiamo garantire lo svuotamento)
//   - flush → true                     → eventId (coda svuotata: consegna tentata con successo)
//   - flush → false / lancia           → "" (meglio vuoto che un id fasullo)
//
// IMPORTANTE: flush=true significa "coda svuotata / POST HTTP riuscito", NON è una
// prova d'ingest lato GlitchTip. La verifica definitiva resta al resolver central
// (F1D): se l'id non esiste davvero, il ticket resta "unverified".
//
// Fail-soft: non lancia MAI e non logga MAI il valore dell'id (nessun dato sensibile).
export async function prepareGlitchtipEventIdForFeedback({
  timeoutMs = 2000,
} = {}) {
  try {
    const eventId = Sentry.lastEventId();
    if (!eventId) return "";
    if (typeof Sentry.flush !== "function") return "";
    const flushed = await Sentry.flush(timeoutMs);
    return flushed ? eventId : "";
  } catch {
    return "";
  }
}

// Opzione `tunnel` per Sentry.init [sc-47917 F2B]. Se la URL è valorizzata, l'SDK
// invia gli envelope a quell'endpoint API first-party (emmamaison-api) invece che
// direttamente all'ingest GlitchTip di terza-origine, aggirando i content-blocker.
// Ritorna `{}` (nessun tunnel) se la URL è assente/vuota: la DSN resta sempre attiva.
export function sentryTunnelOption(tunnelUrl) {
  const u = typeof tunnelUrl === "string" ? tunnelUrl.trim() : "";
  return u ? { tunnel: u } : {};
}
